import {ChangeDetectorRef, Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router, RouterLink, RouterLinkActive} from "@angular/router";
import {
    BellRing,
    BriefcaseBusiness,
    Building2,
    CalendarMinus,
    ChartColumn,
    ChartNoAxesColumn,
    ChartNoAxesGantt,
    ChevronsLeftRightEllipsis,
    ClipboardCheck,
    Columns3,
    FastForward,
    FileText,
    Github,
    LayoutDashboard,
    LucideAngularModule,
    MessageCircle,
    MessageSquare,
    MessageSquareReply, Radar,
    SearchCheck,
    Shield,
    SquareCheckBig,
    SquareDashed,
    StickyNote,
    TriangleAlert,
    User,
    Users,
    Video,
    Zap
} from "lucide-angular";
import {Response} from "../../../models/Response";
import {SharedService} from "../../../services/shared.service";
import {MatDialog} from "@angular/material/dialog";
import {LoginService} from "../../../services/login.service";
import {DashboardService} from "../../dashboard/Services/dashboard.service";
import {SideNavService} from "../../nav-bar/Services/side-nav.service";
import {DevicesService} from "../../../modules/shared/Services/devices.service";
import {FeatureVisibilityControllerService} from "../../../services/feature-visibility-controller.service";
import {WorkItemUtilsService} from "../../../modules/task-management/update-task/Services/work-item-utils.service";
import {DialogConfigService, DialogType} from "../../../modules/shared/Services/dialog-config.service";
import {GetJitsiMeetingNameComponent} from "../get-jitsi-meeting-name-dialogbox/get-jitsi-meeting-name.component";
import {getMessaging, getToken} from "firebase/messaging";
import {environment} from "../../../../environments/environment";
import {FirebaseInsertToken} from "../../dashboard/Models/firebase.model";
import * as moment from "moment";
import {ReminderTabIndex} from "../../dashboard/Models/dashboard.model";
import {RemindersComponent} from "../../dashboard/Components/reminders/reminders.component";
import {AccessDomain} from "../../../models/access-domain";
import {Constants} from "../../../models/constants";
import {ActualURLPaths, FeaturesPaths, RootRoutePaths} from "../../../models/routes-constants";
import {MatIconModule} from "@angular/material/icon";
import {GeoFencePoliciesService} from "../../../modules/geo-fence-admin/services/geo-fence-policies.service";
import {GeoFenceStoreService} from "../../../modules/geo-fence-admin/services/geo-fence-store.service";
import {toSignal} from "@angular/core/rxjs-interop";
import {catchError} from "rxjs/operators";
import {ActiveGeoFenceOrganization} from "../../../models/organization";
import {of} from "rxjs";
import {ReleaseNotesDialogComponent} from "../release-notes-dialog/release-notes-dialog.component";
import {HttpClient} from "@angular/common/http";

@Component({
    selector: 'app-side-nav-panel',
    standalone: true,
    imports: [CommonModule, RouterLinkActive, LucideAngularModule, RouterLink, MatIconModule],
    templateUrl: './side-nav-panel.component.html',
    styleUrls: ['./side-nav-panel.component.scss']
})
export class SideNavPanelComponent {

    loggedInUserDetails: Response;
    loggedInUserName: string | null;
    mobileViewEnabled: boolean;
    desktopViewEnabled: boolean;
    isUserLoggedIn: boolean = false;
    isAdminPanelOpen = false;
    isOtherPanelOpen = false;
    isLeaveWorkflowFeatureAllowed: boolean = false;
    isTimeLineFeatureAllowed: boolean = false;
    isMeetingsWorkflowFeatureAllowed: boolean = false;
    isGroupConversationsFeatureAllowed: boolean = false;
    isJiraUploadFeatureAllowed: boolean = true;
    isEditOrgAllowed: boolean = false;
    readonly FeaturesPaths = FeaturesPaths;
    readonly ActualURLPaths = ActualURLPaths;
    isShowProjectsButton: boolean = false;
    @Output() toggleSidenav = new EventEmitter<unknown>();
    iconsOnlySideNavbar: boolean = false;
    @Input() iconsOnlySideNavbarHover: boolean = false;
    isShowManageSprintBtn: boolean = false;
    isPersonalAccountPresent: boolean = false;
    showSuperUserButton: boolean = false;
    // this method is used to track class of div with class name 'btn-container' in side-nav.component.html
    protected readonly Constants = Constants;
    protected readonly RootRoutePaths = RootRoutePaths;
    protected readonly LayoutDashboard = LayoutDashboard;
    protected readonly SquareCheckBig = SquareCheckBig;
    protected readonly SearchCheck = SearchCheck;
    protected readonly Video = Video;
    protected readonly Columns3 = Columns3;
    protected readonly ChartNoAxesColumn = ChartNoAxesColumn;
    protected readonly ChartColumn = ChartColumn;
    protected readonly ChevronsLeftRightEllipsis = ChevronsLeftRightEllipsis;
    protected readonly Users = Users;
    protected readonly BriefcaseBusiness = BriefcaseBusiness;
    protected readonly Zap = Zap;
    protected readonly FastForward = FastForward;
    protected readonly Building2 = Building2;
    protected readonly User = User;
    protected readonly Shield = Shield;
    protected readonly Github = Github;
    protected readonly MessageSquareReply = MessageSquareReply;
    protected readonly MessageCircle = MessageCircle;
    protected readonly MessageSquare = MessageSquare;
    protected readonly SquareDashed = SquareDashed;
    protected readonly ClipboardCheck = ClipboardCheck;
    protected readonly Radar = Radar;
    protected readonly ChartNoAxesGantt = ChartNoAxesGantt;
    protected readonly BellRing = BellRing;
    protected readonly TriangleAlert = TriangleAlert;
    protected readonly StickyNote = StickyNote;
    protected readonly CalendarMinus = CalendarMinus;
    protected readonly FileText = FileText;

    // injecting services
    private geoFencePoliciesService = inject(GeoFencePoliciesService);
    private geoFenceStore = inject(GeoFenceStoreService);

    constructor(
        public sharedService: SharedService,
        private router: Router,
        private dialog: MatDialog,
        private loginService: LoginService,
        private dashboardService: DashboardService,
        private cdr: ChangeDetectorRef,
        private sideNavService: SideNavService,
        private deviceService: DevicesService,
        public featureVisibilityControllerService: FeatureVisibilityControllerService,
        private updateTaskFunctionsService: WorkItemUtilsService,
        private dialogConfigService: DialogConfigService,
        private http: HttpClient,
    ) {
        this.mobileViewEnabled = this.deviceService.isMobile();
        this.desktopViewEnabled = this.deviceService.isDesktop();
    }

    private geoFenceData = toSignal(
        this.geoFencePoliciesService.getGeoFenceActiveDetails().pipe(
            catchError(() => of({ data: [] as ActiveGeoFenceOrganization[] }))
        ),
        { initialValue: { data: [] as ActiveGeoFenceOrganization[] } }
    );
    // Expose the state as a signal - auto-updates, no manual subscription
    protected isGeoFenceActive = this.geoFenceStore.isActive;
    protected releaseVersion: string = '';

    ngOnInit(): void {
        this.sideNavService.sidenavOpen$.subscribe((open) => {
            this.iconsOnlySideNavbar = !open;
        });
        this.loginService.isUserLoggedIn$.subscribe(() => {
            this.isUserLoggedIn = this.loginService.isLoggedIn();
            this.isPersonalAccountPresent = this.sharedService.isUserRegisteredInPersonalOrganization();
            this.cdr.detectChanges();
        });

        if (this.sharedService.isUserTokenExpired()) {
            this.router.navigate([RootRoutePaths.HOME]);
        } else {
            this.loggedInUserDetails = new Response();
            this.getLoggedInUserName();
            this.validateFCMToken();
        }

        this.isLeaveWorkflowFeatureAllowed = this.featureVisibilityControllerService.isFeatureAllowed(
            RootRoutePaths.LEAVE_WORKFLOW,
        );
        this.isTimeLineFeatureAllowed = this.featureVisibilityControllerService.isFeatureAllowed(
            RootRoutePaths.TIMELINE_VIEW,
        );
        this.isMeetingsWorkflowFeatureAllowed = this.featureVisibilityControllerService.isFeatureAllowed(
            RootRoutePaths.MEETINGS_WORKFLOW,
        );
        this.isGroupConversationsFeatureAllowed = this.featureVisibilityControllerService.isFeatureAllowed(
            RootRoutePaths.ENTITY_COMMENTS,
        );
        this.isEditOrgAllowed = this.featureVisibilityControllerService.isFeatureAllowed(
            FeaturesPaths.MANAGE_ORGANIZATION,
        );
        this.isJiraUploadFeatureAllowed = this.featureVisibilityControllerService.isFeatureAllowed(
            RootRoutePaths.JIRA_UPLOAD,
        );
        this.isShowProjectsButton = this.sharedService.isUserHasAdminAccessInAnyOrg();
        this.decideToShowManageSprintButton();
        this.showSuperUserButton = this.sharedService.isUserSuperAdmin();

        this.loadReleaseVersion();
    }

    getLoggedInUserName() {
        this.loggedInUserName = this.sharedService.getLoggedInUserName();
    }

    openUpdateTaskDialog() {
        this.updateTaskFunctionsService.openGetTaskNumberDialogAndOpenUpdateTaskDialog();
    }

    openMeetingDialog() {
        const dialogRef = this.dialog.open(GetJitsiMeetingNameComponent);
        dialogRef.afterClosed().subscribe((event) => {
            if (event != null) {
                this.goToJitsiMeeting(event.meetingName, event.meetingToken, event.isAnotherMeetingScheduledToday);
            }
        });
    }

    goToJitsiMeeting(meetingName: string, meetingToken: string, isAnotherMeetingScheduledToday: boolean) {
        if (meetingName.length > 0) {
            const url = this.router.serializeUrl(
                this.router.createUrlTree([RootRoutePaths.MEET], {
                    queryParams: {
                        meetingName: meetingName,
                        token: meetingToken,
                        isAnotherMeetingScheduledToday: isAnotherMeetingScheduledToday,
                    },
                }),
            );
            window.open(url, "_blank");
        }
    }

    /** This Method will be called to check isFCMTokenValidated and Push Notification Permission Granted or not.*/

    validateFCMToken() {
        if (!this.sharedService.isFCMTokenValidated && "serviceWorker" in navigator && window.isSecureContext) {
            this.requestPermission();
        } else {
            console.log("FCM Token is already validated");
        }
    }

    requestPermission() {
        const messaging = getMessaging();
        getToken(messaging, {vapidKey: environment.firebase.vapidKey})
            .then((currentToken) => {
                if (currentToken) {
                    /** Detect the token is Exist or not for current device */
                    this.dashboardService.detectFirebaseToken("web", this.sharedService.getDeviceId()!).subscribe(
                        (response: any) => {
                            let detectedToken: string = response.data;
                            if (detectedToken == currentToken) {
                                this.sharedService.isFCMTokenValidated = true;
                            } else {
                                let fcmInsertTokenRequestBody = new FirebaseInsertToken();
                                console.log(fcmInsertTokenRequestBody);
                                fcmInsertTokenRequestBody.deviceId = this.sharedService.getDeviceId()!;
                                fcmInsertTokenRequestBody.token = currentToken;
                                fcmInsertTokenRequestBody.timestamp =
                                    moment(new Date()).format("YYYY-MM-DD") +
                                    "T" +
                                    moment(new Date()).format("HH:mm:ss");
                                this.dashboardService.insertFirebaseToken(fcmInsertTokenRequestBody).subscribe(
                                    () => {
                                        console.log("FCM Token Inserted Successfully");
                                        this.sharedService.isFCMTokenValidated = true;
                                    },
                                    () => {
                                        console.log("FCM Token Insertion Failed");
                                        this.sharedService.isFCMTokenValidated = false;
                                    },
                                );
                            }
                        },
                        () => {
                            console.log("FCM Token Detection Failed");
                            /** If token is not exist then insert the token in Backend */
                            let fcmInsertTokenRequestBody = new FirebaseInsertToken();
                            fcmInsertTokenRequestBody.deviceId = this.sharedService.getDeviceId()!;
                            fcmInsertTokenRequestBody.token = currentToken;
                            fcmInsertTokenRequestBody.timestamp =
                                moment(new Date()).format("YYYY-MM-DD") + "T" + moment(new Date()).format("HH:mm:ss");
                            this.dashboardService.insertFirebaseToken(fcmInsertTokenRequestBody).subscribe(
                                () => {
                                    console.log("FCM Token Inserted Successfully");
                                    this.sharedService.isFCMTokenValidated = true;
                                },
                                () => {
                                    console.log("FCM Token Insertion Failed");
                                    this.sharedService.isFCMTokenValidated = false;
                                },
                            );
                        },
                    );
                } else {
                    console.log("No registration token available. Request permission to generate one.");
                }
            })
            .catch((err) => {
                console.log("An error occurred while retrieving token. ", err);
            });
    }

    toggleAdminMenu() {
        this.isAdminPanelOpen = !this.isAdminPanelOpen;
        this.cdr.detectChanges();
    }

    toggleOtherMenu() {
        this.isOtherPanelOpen = !this.isOtherPanelOpen;
        this.cdr.detectChanges();
    }

    getHomeRouterLink() {
        return this.isUserLoggedIn ? FeaturesPaths.DASHBOARD : RootRoutePaths.HOME;
    }

    checkIsRouteActive(route: string): boolean {
        const currentUrl = this.router.url.split('?')[0];
        const normalizedRoute = '/' + route.replace(/^\//, '');
        return currentUrl === normalizedRoute || currentUrl.startsWith(normalizedRoute + '/');
    }

    openRemindersDialog() {
        const dialogWidth =
            this.deviceService.isTablet() || this.deviceService.isMobile() ? DialogType.LARGE : DialogType.MEDIUM;
        const dialogConfig = this.dialogConfigService.getConfig(dialogWidth, {
            reminderTabIndex: ReminderTabIndex.TODAY,
        });

        this.dialog
            .open(RemindersComponent, dialogConfig)
            .afterClosed()
            .subscribe({
                next: (data) => {
                    console.log("Data from dialog", data);
                },
            });
    }

    private decideToShowManageSprintButton() {
        let accessDomains: AccessDomain[] = this.sharedService.getLoggedInUserAccessDomains() as AccessDomain[];
        this.isShowManageSprintBtn = accessDomains?.some((accessDomain: AccessDomain) => accessDomain.roleId !== 102);
    }

    private loadReleaseVersion(): void {
        this.http.get<{ version: string }>('assets/release-notes.json')
            .subscribe({
                next: (data) => {
                    this.releaseVersion = data.version;
                    this.cdr.detectChanges();
                },
                error: (err) => {
                    console.error('Failed to load release version:', err);
                    this.releaseVersion = 'v0.0.0';
                }
            });
    }

    openReleaseNotes(): void {
        this.dialog.open(ReleaseNotesDialogComponent, {
            width: this.deviceService.isMobile() ? '95vw' : '600px',
            autoFocus: false,
            data: {
                version: this.releaseVersion
            }
        });
    }
}
